import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Stack, Button, CircularProgress, Alert } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

// Backend supports one-to-many, but this UI currently manages one-to-one.
const ENABLE_ONE_TO_MANY_STAFF_EMAILS = false;

const EditEmailsModal = ({ staff, open, onClose, onSaved }) => {
  const [originalEmails, setOriginalEmails] = useState([]);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !staff) return;

    const fetchEmails = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axiosInstance.get(`api/webhooks/staff-users/${staff.id}/emails`);

        const list = res.data?.data || [];

        // UI manages one-to-one for now.
        const managedEmails = ENABLE_ONE_TO_MANY_STAFF_EMAILS ? list : list.slice(0, 1);

        setOriginalEmails(managedEmails);
        setEmails(managedEmails.map((e) => e.email));
      } catch (err) {
        console.error("error getting staff emails:", err);
        setError("Couldn't load emails for this staff member.");
      } finally {
        setLoading(false);
      }
    };

    fetchEmails();
  }, [open, staff]);

  const handleAddEmail = () => {
    const cleaned = newEmail.trim().toLowerCase();

    if (!cleaned) return;

    if (!EMAIL_PATTERN.test(cleaned)) {
      setError("Enter a valid email address.");
      return;
    }

    if (!ENABLE_ONE_TO_MANY_STAFF_EMAILS && emails.length >= 1) {
      setError("Only one email can be assigned to a staff user.");
      return;
    }

    if (emails.includes(cleaned)) {
      setError("That email is already assigned.");
      return;
    }

    setEmails((prev) => [...prev, cleaned]);
    setNewEmail("");
    setError("");
  };

  const handleRemoveEmail = (email) => {
    setEmails((prev) => prev.filter((e) => e !== email));
    setError("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleSave = async () => {
    const originalList = originalEmails.map((e) => e.email.toLowerCase());

    const currentList = emails.map((e) => e.toLowerCase());

    const toAdd = currentList.filter((email) => !originalList.includes(email));

    const toRemove = originalList.filter((email) => !currentList.includes(email));

    if (!toAdd.length && !toRemove.length) {
      onClose();
      return;
    }

    try {
      setSaving(true);
      setError("");

      if (toAdd.length) {
        await axiosInstance.post(`api/webhooks/staff-users/${staff.id}/emails`, {
          emails: toAdd,
        });
      }

      if (toRemove.length) {
        await axiosInstance.delete(`api/webhooks/staff-users/${staff.id}/emails`, {
          data: {
            emails: toRemove,
          },
        });
      }

      // Refresh parent table first
      if (onSaved) {
        await onSaved();
      }

      onClose();
    } catch (err) {
      console.error("error saving staff emails:", err);

      setError(err.response?.data?.message || "Couldn't save changes. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        Emails for {staff?.staff_name || "staff member"}
        <IconButton onClick={onClose} size="small" disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <TextField size="small" fullWidth placeholder="Add an email" value={newEmail} disabled={!ENABLE_ONE_TO_MANY_STAFF_EMAILS && emails.length >= 1} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={handleKeyDown} />

              <Button variant="outlined" onClick={handleAddEmail} disabled={!ENABLE_ONE_TO_MANY_STAFF_EMAILS && emails.length >= 1}>
                Add
              </Button>
            </Stack>

            <Stack direction="row" flexWrap="wrap" gap={1}>
              {emails.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No email assigned yet.
                </Typography>
              )}

              {emails.map((email) => (
                <Chip key={email} label={email} onDelete={() => handleRemoveEmail(email)} size="small" />
              ))}
            </Stack>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSave} disabled={loading || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StaffUsers = () => {
  const [staffUsers, setStaffUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const getStaffUsers = async () => {
    try {
      setLoading(true);

      const res = await axiosInstance.get("api/webhooks/staff-users");

      if (res.status === 200) {
        setStaffUsers(res.data.data || []);
      }
    } catch (error) {
      console.error("error getting staff users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getStaffUsers();
  }, []);

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
    },
    {
      field: "staff_name",
      headerName: "Staff Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "staff_email",
      headerName: "Staff Email",
      flex: 1,
      minWidth: 220,
    },
    {
      field: "assigned_email",
      headerName: "Assigned Email",
      flex: 1,
      minWidth: 240,
      sortable: false,
      filterable: false,
      renderCell: (params) => <p>{params.value || "-"}</p>,
    },
    ,
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <IconButton size="small" onClick={() => setEditingStaff(params.row)} aria-label="Edit assigned email">
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    ,
  ];

  const rows = staffUsers.map((staff) => {
    const staffEmails = staff.staffUserEmails || [];

    // Backend relation is one-to-many,
    // but frontend currently uses only the first email.
    const assignedEmail = staffEmails.length > 0 ? staffEmails[0]?.email : null;

    return {
      id: staff.id,
      staff_name: staff.staff_name || "-",
      staff_email: staff.staff_email || "-",
      assigned_email: assignedEmail || null,
    };
  });

  return (
    <Box sx={{ width: "100%", p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Staff Users
      </Typography>

      <Box sx={{ width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          autoHeight
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 50,
                page: 0,
              },
            },
          }}
          disableRowSelectionOnClick
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f3f4f6",
              color: "#374151",
              fontWeight: 600,
            },

            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#f3f4f6",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f9fafb",
            },

            // Remove cell focus outline/highlight
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },

            "& .MuiDataGrid-cell:focus-within": {
              outline: "none",
            },

            // Remove row selection background
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "transparent",
            },

            "& .MuiDataGrid-row.Mui-selected:hover": {
              backgroundColor: "#f9fafb",
            },
          }}
        />
      </Box>

      <EditEmailsModal staff={editingStaff} open={Boolean(editingStaff)} onClose={() => setEditingStaff(null)} onSaved={getStaffUsers} />
    </Box>
  );
};

export default StaffUsers;
